import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { MoreHorizontal, ChevronUp, ChevronDown, X, Search } from "lucide-react";

// ---- helpers ----
export const timeAgo = (iso) => {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const s = Math.floor((Date.now() - then) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr${h > 1 ? "s" : ""} ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} day${d > 1 ? "s" : ""} ago`;
  const mo = Math.floor(d / 30);
  return `${mo} mo ago`;
};

export const datetimeFromIso = (iso) => {
  if (!iso) return "—";
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
};

// ---- primitives ----
export const Spinner = ({ className = "" }) => (
  <div className={`h-5 w-5 animate-spin rounded-full border-2 border-[#e5e5e5] border-t-[#111111] ${className}`} />
);

export const PageLoader = () => (
  <div className="flex h-[60vh] items-center justify-center">
    <Spinner className="h-7 w-7" />
  </div>
);

export const AdminCard = ({ children, className = "", testid, ...props }) => (
  <div data-testid={testid} className={`rounded-[16px] border border-[#e9e6e1] bg-white ${className}`} {...props}>
    {children}
  </div>
);

export const PageHeader = ({ title, subtitle, right }) => (
  <div className="mb-7 flex items-start justify-between gap-4">
    <div>
      <h1 className="text-[26px] font-semibold tracking-tight text-[#111111]">{title}</h1>
      {subtitle && <p className="mt-1 text-[14px] text-[#777]">{subtitle}</p>}
    </div>
    {right}
  </div>
);

export const StatCard = ({ label, value, delta, hint, testid }) => (
  <AdminCard className="p-5" data-testid={testid}>
    <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#999]">{label}</p>
    <p className="mt-2 text-[30px] font-bold leading-none tracking-tight text-[#111111]">{value}</p>
    {delta !== null && delta !== undefined && (
      <p className="mt-2 text-[12px] font-medium text-[#1a7a45]">+{delta} new · 30d</p>
    )}
    {hint && <p className="mt-2 text-[12px] text-[#999]">{hint}</p>}
  </AdminCard>
);

const TONES = {
  green: "bg-[#e8f3ec] text-[#1a7a45]",
  red: "bg-[#fbe9e7] text-[#c0392b]",
  amber: "bg-[#fdf3e3] text-[#9a6a16]",
  gray: "bg-[#f2f0ec] text-[#666]",
  dark: "bg-[#111111] text-white",
};

const STATUS_TONE = {
  confirmed: "green", completed: "dark", cancelled: "red", no_show: "amber", pending: "gray",
  paid: "green", refunded: "gray", failed: "red", sent: "green", delivered: "green",
  active: "green", free: "gray", suspended: "red",
  Operational: "green", Degraded: "amber", Down: "red", "Pending Setup": "gray",
};

const STATUS_LABEL = {
  no_show: "No-show", pending: "Pending", confirmed: "Confirmed", completed: "Completed",
  cancelled: "Cancelled", paid: "Paid", refunded: "Refunded", failed: "Failed", sent: "Sent",
  active: "Active", free: "Free", suspended: "Suspended",
};

export const StatusPill = ({ status, label, tone }) => {
  const t = tone || STATUS_TONE[status] || "gray";
  const text = label || STATUS_LABEL[status] || status;
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ${TONES[t]}`}>
      {text}
    </span>
  );
};

export const SearchInput = ({ value, onChange, placeholder, testid }) => (
  <div className="relative w-full max-w-[360px]">
    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#aaa]" />
    <input
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="h-10 w-full rounded-[10px] border border-[#e5e5e5] bg-white pl-9 pr-3 text-[14px] text-[#111] outline-none transition-colors placeholder:text-[#aaa] focus:border-[#111]"
    />
  </div>
);

export const FilterChips = ({ options, value, onChange, testidPrefix = "filter" }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((o) => (
      <button
        key={o}
        data-testid={`${testidPrefix}-${o.replace(/\s+/g, "-").toLowerCase()}`}
        onClick={() => onChange(o)}
        className={`h-9 rounded-full px-3.5 text-[13px] font-medium transition-colors ${
          value === o ? "bg-[#111111] text-white" : "bg-[#f2f0ec] text-[#666] hover:bg-[#eceae5]"
        }`}
      >
        {o}
      </button>
    ))}
  </div>
);

// ---- sortable table ----
export const DataTable = ({ columns, rows, onRowClick, empty = "No records found.", testid }) => {
  const [sortKey, setSortKey] = useState(null);
  const [dir, setDir] = useState("asc");

  const sorted = [...rows];
  if (sortKey) {
    sorted.sort((a, b) => {
      const av = a[sortKey] ?? "";
      const bv = b[sortKey] ?? "";
      if (av < bv) return dir === "asc" ? -1 : 1;
      if (av > bv) return dir === "asc" ? 1 : -1;
      return 0;
    });
  }

  const toggleSort = (key) => {
    if (sortKey === key) setDir(dir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setDir("asc"); }
  };

  return (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="w-full border-collapse text-left">
        <thead>
          <tr className="border-b border-[#e9e6e1]">
            {columns.map((c) => (
              <th
                key={c.key}
                onClick={() => c.sortable !== false && toggleSort(c.key)}
                className={`whitespace-nowrap px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#999] ${
                  c.sortable !== false ? "cursor-pointer select-none hover:text-[#111]" : ""
                }`}
              >
                <span className="inline-flex items-center gap-1">
                  {c.label}
                  {sortKey === c.key && (dir === "asc" ? <ChevronUp size={13} /> : <ChevronDown size={13} />)}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="px-4 py-12 text-center text-[14px] text-[#999]">
                {empty}
              </td>
            </tr>
          )}
          {sorted.map((row, i) => (
            <tr
              key={row.id || i}
              onClick={() => onRowClick && onRowClick(row)}
              className={`border-b border-[#f2efea] text-[14px] text-[#222] transition-colors hover:bg-[#faf8f5] ${
                onRowClick ? "cursor-pointer" : ""
              } ${row._accent ? "border-l-2 border-l-[#c0392b]" : ""}`}
            >
              {columns.map((c) => (
                <td key={c.key} className="whitespace-nowrap px-4 py-3.5 align-top">
                  {c.render ? c.render(row) : row[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ---- row action menu (three-dot) ----
export const RowMenu = ({ items, testid }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const menuItems = items.filter(Boolean);

  const placeMenu = useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const menuW = 208;
    const menuH = menuItems.length * 42 + 8;
    let top = r.bottom + 4;
    let left = r.right - menuW;
    if (left < 8) left = 8;
    if (top + menuH > window.innerHeight - 8) top = Math.max(8, r.top - menuH - 4);
    setPos({ top, left });
  }, [menuItems.length]);

  useEffect(() => {
    const close = (e) => {
      if (btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  useEffect(() => {
    if (!open) return;
    placeMenu();
    const onRelayout = () => setOpen(false);
    window.addEventListener("scroll", onRelayout, true);
    window.addEventListener("resize", onRelayout);
    return () => {
      window.removeEventListener("scroll", onRelayout, true);
      window.removeEventListener("resize", onRelayout);
    };
  }, [open, placeMenu]);

  return (
    <>
      <div className="inline-block" onClick={(e) => e.stopPropagation()}>
        <button
          ref={btnRef}
          data-testid={testid}
          type="button"
          aria-expanded={open}
          onClick={() => {
            if (!open) placeMenu();
            setOpen((o) => !o);
          }}
          className="flex h-8 w-8 items-center justify-center rounded-full text-[#666] hover:bg-black/5"
        >
          <MoreHorizontal size={18} />
        </button>
      </div>
      {open && createPortal(
        <div
          ref={menuRef}
          role="menu"
          className="fixed z-50 w-52 overflow-hidden rounded-[12px] border border-[#e9e6e1] bg-white py-1 shadow-lg"
          style={{ top: pos.top, left: pos.left }}
          onClick={(e) => e.stopPropagation()}
        >
          {menuItems.map((it) => (
            <button
              key={it.label}
              type="button"
              role="menuitem"
              data-testid={it.testid}
              onClick={() => { setOpen(false); it.onClick(); }}
              className={`block w-full px-4 py-2.5 text-left text-[13px] font-medium transition-colors hover:bg-[#faf8f5] ${
                it.danger ? "text-[#c0392b]" : "text-[#222]"
              }`}
            >
              {it.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
};

// ---- overlays ----
export const Drawer = ({ open, onClose, title, children, testid }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40" data-testid={testid}>
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="absolute right-0 top-0 flex h-full w-full max-w-[480px] flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-[#e9e6e1] px-6 py-4">
          <h3 className="text-[16px] font-semibold text-[#111]">{title}</h3>
          <button data-testid="drawer-close" onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-black/5">
            <X size={18} />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>
      </div>
    </div>
  );
};

export const Modal = ({ open, onClose, title, children, testid }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-[480px] overflow-hidden rounded-[18px] bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-[#e9e6e1] px-6 py-4">
          <h3 className="text-[16px] font-semibold text-[#111]">{title}</h3>
          <button data-testid="modal-close" onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-black/5">
            <X size={18} />
          </button>
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
};

export const ConfirmDialog = ({ open, onClose, onConfirm, title, message, confirmLabel = "Confirm", danger = true, busy = false }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="confirm-dialog">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-[420px] rounded-[18px] bg-white p-6 shadow-2xl">
        <h3 className="text-[17px] font-semibold text-[#111]">{title}</h3>
        <p className="mt-2 text-[14px] leading-relaxed text-[#666]">{message}</p>
        <div className="mt-6 flex items-center justify-end gap-3">
          <button onClick={onClose} className="text-[14px] font-medium text-[#666] hover:text-[#111]" data-testid="confirm-cancel">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            data-testid="confirm-action"
            className={`flex h-11 items-center justify-center gap-2 rounded-[12px] px-5 text-[14px] font-semibold text-white disabled:opacity-60 ${
              danger ? "bg-[#c0392b]" : "bg-[#111111]"
            }`}
          >
            {busy && <Spinner className="h-4 w-4 border-white/40 border-t-white" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.06em] text-[#999]">{label}</span>
    {children}
  </label>
);

export const Input = (props) => (
  <input
    {...props}
    className="h-11 w-full rounded-[10px] border border-[#e5e5e5] bg-white px-3.5 text-[14px] text-[#111] outline-none focus:border-[#111]"
  />
);

export const Textarea = (props) => (
  <textarea
    {...props}
    className="w-full rounded-[10px] border border-[#e5e5e5] bg-white p-3.5 text-[14px] text-[#111] outline-none focus:border-[#111]"
  />
);

export const Btn = ({ children, onClick, disabled, busy, variant = "dark", testid, type = "button" }) => (
  <button
    type={type}
    data-testid={testid}
    onClick={onClick}
    disabled={disabled || busy}
    className={`flex h-11 items-center justify-center gap-2 rounded-[12px] px-5 text-[14px] font-semibold transition-all disabled:opacity-50 ${
      variant === "dark" ? "bg-[#111111] text-white" : "border border-[#111111] bg-white text-[#111111]"
    }`}
  >
    {busy && <Spinner className="h-4 w-4 border-white/40 border-t-white" />}
    {children}
  </button>
);
